// src/pages/admin/components/DeviceForm.js
import React, { useState, useEffect } from 'react';

function DeviceForm({ device, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    image: '',
    type: 'phone'
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  // Device types (must match backend models)
  const deviceTypes = [
    { value: 'phone', label: 'Phone' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'watch', label: 'Watch' },
    { value: 'console', label: 'Console' }
  ];
  
  // Allowed brands for each device type (must match backend data exactly)
  const allowedBrands = {
    phone: [
      'Apple',
      'Samsung',
      'Google Pixel',
      'OnePlus',
      'Huawei',
      'Nokia',
      'Motorola',
      'Oppo',
      'Redmi'
    ],
    tablet: [
      'Apple',
      'Samsung'
    ],
    watch: [
      'Apple',
      'Samsung'
    ],
    console: [
      'PlayStation',
      'Xbox',
      'Nintendo'
    ]
  };
  
  useEffect(() => {
    if (device) {
      setFormData({
        brand: device.brand || '',
        model: device.model || '',
        image: device.image || '',
        type: device.type || 'phone'
      });
      
      if (device.image) {
        setPreviewUrl(device.image);
      } else {
        setPreviewUrl('');
      }
    }
  }, [device]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Reset brand if device type changes and current brand is not valid for new type
      if (name === 'type' && prev.brand && !allowedBrands[value]?.includes(prev.brand)) {
        newData.brand = '';
      }
      
      return newData;
    });
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image file must be less than 2MB');
      return;
    }
    
    setImageFile(file);
    setError('');
    
    // Create preview
    const fileReader = new FileReader();
    fileReader.onload = () => {
      setPreviewUrl(fileReader.result);
    };
    fileReader.readAsDataURL(file);
  };
  
  const uploadImage = async () => {
    if (!imageFile) return formData.image; // Return existing image URL if no new image
    
    setUploading(true);
    setError('');
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', imageFile);
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload image');
      }
      
      const data = await response.json();
      return data.imageUrl;
    } catch (err) {
      setError('Error uploading image. Please try again.');
      console.error('Upload error:', err);
      return null;
    } finally {
      setUploading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model || !formData.type) {
      setError('Please fill in all required fields');
      return;
    }
    
    setError('');
    
    // Upload image if a new one was selected
    if (imageFile) {
      setUploading(true);
      const imageUrl = await uploadImage();
      setUploading(false);
      
      if (!imageUrl) {
        // Error occurred during upload
        return;
      }
      
      // Submit with updated image URL
      onSubmit({
        ...formData,
        image: imageUrl
      });
    } else {
      // Submit with existing data
      onSubmit(formData);
    }
  };
  
  return (
    <div className="admin-form device-form-modal">
      <div className="form-header">
        <h2>{device ? 'Edit Device' : 'Add New Device'}</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="type">
            Device Type <span className="required">*</span>
          </label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            disabled={!!device || uploading}
          >
            {deviceTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="brand">
            Brand <span className="required">*</span>
          </label>
          <select
            id="brand"
            name="brand"
            value={formData.brand}
            onChange={handleChange}
            required
            disabled={uploading}
          >
            <option value="">Select a brand</option>
            {allowedBrands[formData.type]?.map(brand => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="model">
            Model <span className="required">*</span>
          </label>
          <input
            type="text"
            id="model"
            name="model"
            value={formData.model}
            onChange={handleChange}
            required
            disabled={uploading}
            placeholder="e.g., iPhone 13, Galaxy S23, Pixel 7"
          />
        </div>
        
        <div className="form-group">
          <label>Device Image</label>
          
          <div className="image-upload-container">
            {previewUrl && (
              <div className="image-preview">
                <img src={previewUrl} alt="Preview" />
              </div>
            )}
            
            <div className="upload-controls">
              <input
                type="file"
                id="image-upload"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="image-upload" 
                className="btn-admin btn-admin-secondary"
              >
                {uploading ? 'Uploading...' : 'Choose Image'}
              </label>
            </div>
          </div>
          
          <p className="help-text">
            Maximum file size: 2MB. Supported formats: JPEG, PNG, WebP
          </p>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel} 
            className="btn-admin btn-admin-secondary"
            disabled={uploading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-admin btn-admin-primary"
            disabled={uploading}
          >
            {uploading ? 'Saving...' : (device ? 'Update Device' : 'Add Device')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DeviceForm;